<template>
    <v-card
        class="product-list-item pt-5 pb-2 px-2 d-flex flex-column flex-grow-1 h-100"
        elevation="1"
        itemtype="https://schema.org/Product"
        itemscope
    >
        <div
            class="d-none"
            itemtype="https://schema.org/Brand"
            itemprop="brand"
            itemscope
        >
            <meta itemprop="name" :content="brandTitle" />
        </div>

        <ProductFlags
            v-if="item.flags"
            class="card-top-product-flags"
            v-model="item.flags"
            density="compact"
            size="large"
            label
        ></ProductFlags>

        <NuxtLink
            :to="itemUrl"
            class="d-block aspect-ratio-1"
        >
            <ProductsImage
                class="w-100 d-table"
                :key="`prod-img-id-${selectedSku?.defaultImage?.uid || useId()}`"
                :image-object="selectedSku?.defaultImage"
                :type="['small', 'medium']"
            ></ProductsImage>
        </NuxtLink>

        <div
            class="position-relative d-flex flex-column fill-height mt-6"
        >

            <v-divider
                class="mb-6"
            ></v-divider>

            <DebugContent>
                <details
                    class="mb-2"
                >
                    <summary
                        class="cursor-pointer"
                    >
                        Options Debug Info
                    </summary>
                    <div>
                        <h5>Model</h5>
                        <ul>
                            <li
                                v-for="(v, k) in optionsModel"
                                :key="`optionsModel-opt-${k}`"
                            >
                                <strong>{{ k }}:</strong>
                                {{ v }}
                            </li>
                        </ul>

                        <h5>Options List</h5>
                        <ul>
                            <li
                                v-for="(opt, i) in optionsList"
                                :key="`optionsList-opt-${i}`"
                                :class="{
                                    'text-success': opt.showOnCollectionPages
                                }"
                            >
                                {{ opt.attribute.uid }}
                            </li>
                        </ul>

                        <h5>Disabled Model</h5>
                        <ul>
                            <li
                                v-for="(opt, i) in optionsDisabledModel"
                                :key="`optionsDisabledModel-opt-${i}`"
                            >
                                {{ opt }}
                            </li>
                        </ul>
                    </div>
                </details>
            </DebugContent>

            <!-- Options -->
            <ProductsListOptions
                :options-model="optionsModel"
                :options-list="optionsList"
                :options-disabled-model="optionsDisabledModel"
                @on-change="onOptionChange"
                hide-label
            ></ProductsListOptions>

            <NuxtLink
                :to="itemUrl"
                class="text-decoration-none d-flex flex-column flex-grow-1"
            >
                <!-- Item Title -->

                <div
                    class="product-list-item-title mt-4 mb-2 pl-2 flex-grow-1"
                >
                    <ProductTitle
                        :product="item"
                        :compact="true"
                        :font-size-px="24"
                        no-bold
                    ></ProductTitle>
                    <ProductRatingTitle
                        :average="item.averageRating"
                        :total="item.reviewCount"
                    ></ProductRatingTitle>

                    <meta itemprop="description" :content="item.description" />
                </div>

                <!-- Price -->
                <div
                    v-if="selectedSku"
                    class="mt-3  d-flex flex-column pl-2"
                >
                    <span
                        v-if="currentMinPrice"
                        class="v-align-middle"
                    >
                        from <span class="font-weight-medium font-size-6">{{ formatCurrency(currentMinPrice) }}</span>
                    </span>
                    <ProductPriceInfo
                        v-else
                        class="d-flex flex-column"
                        :current-sku="selectedSku"
                        size="sm"
                        order-reverse
                        no-bold
                    ></ProductPriceInfo>
                </div>
            </NuxtLink>

        </div>
    </v-card>
</template>

<script lang="ts" setup>
    import type {
        Product,
        Sku,
        SkusMap,
        ProductAttrsMaps,
        ProductAttrsGroupOption,
        ProductOptionsModel,
        ProductAttrsDisabledModel
    } from '~/types';

    import {
        useSkusUtils,
        useProductAttrs,
    } from '~/composables';
    import {
        useCustomNuxtUtils
    } from 'x1-base/composables';

    import { pathJoin, formatCurrency } from 'x1-base/utils';
    import ProductTitle from '~/components/products/product/ProductTitle/ProductTitle.vue';
    import ProductRatingTitle from '~/components/products/ProductRatingTitle/ProductRatingTitle.vue';
    import ProductFlags from '~/components/products/ProductFlags/ProductFlags.vue';
    import ProductsImage from '~/components/products/ProductsImage/ProductsImage.vue';
    import ProductPriceInfo from '~/components/products/ProductPriceInfo/ProductPriceInfo.vue';

    import ProductsListOptions from '~/components/products/products-list/ProductsListOptions/ProductsListOptions.vue';
    import DebugContent from '~/components/DebugContent/DebugContent.vue';
    import { isUndefined as lo_isUndefined } from 'es-toolkit';
    import {
        ProductListItemStateKeyPrefix
    } from '~/constants';

    const props = withDefaults(
        defineProps<{
            item: Product;
            selectFirst: boolean;
            brandTitle: string;
            pageKey: string;
        }>(),
        {
            selectFirst: false
        }
    );

    const {
        getSkusMap,
        getSkuByUid,
        getValuesModelBySku,
        findFirstSkuByPartialOptions
    } = useSkusUtils();

    const {
        processAttrsGroups
    } = useProductAttrs();

    const {
        removeOnceKey
    } = useCustomNuxtUtils();

    const debugConsole = useDebug();

    const {
        skus,
        optionGroups,
        defaultSkuUid,
        uid: itemUid
    } = props.item;


    const selectedSku = useState<Nilish<Sku>>(`${ProductListItemStateKeyPrefix}SelectedSku_${itemUid}`, () => null);
    const itemUrl = computed<string>(() => pathJoin('/', selectedSku.value?.path || ''));
    const skusMap = useState<Nilish<SkusMap>>(`${ProductListItemStateKeyPrefix}SkusMap_${itemUid}`, () => null);
    const currentMinPrice = computed<Nullable<number>>(() => !lo_isUndefined(props.item?.minPrice) ? props.item.minPrice : null);

    const attrsMapOptions = useState<Nilish<ProductAttrsMaps>>(`${ProductListItemStateKeyPrefix}AttrsMapOpts_${itemUid}`, () => null);
    const optionsDisabledModel = ref<ProductAttrsDisabledModel>({});
    // const optionsInvalidValuesCombos = useState<string[][]>('keyProdListFormOptInvValCombos', () => []);

    const optionsList = ref<ProductAttrsGroupOption[]>(optionGroups);
    const optionsModel = useState<ProductOptionsModel>(`${ProductListItemStateKeyPrefix}OptsModel_${itemUid}`, () => ({}));
    const attrsWhitelist = ref<string[]>([]);

    const onceKey = `${props.pageKey}_${itemUid}`;

    callOnce(onceKey, () => {
        skusMap.value = getSkusMap(skus);
        attrsMapOptions.value = processAttrsGroups(optionGroups);
        attrsWhitelist.value = optionGroups.reduce((acc: string[], opt) => {
            if (opt.showOnCollectionPages) acc.push(opt.attribute.uid);
            return acc;
        }, []);


        selectedSku.value = getSkuByUid(skus, defaultSkuUid) || skus[0];
        const skuUid = selectedSku.value?.uid;

        if (skuUid) {
            optionsModel.value = getValuesModelBySku(skusMap.value, attrsMapOptions.value.attrs, skuUid);
        }
    }, { mode: 'navigation' });

    function onOptionChange(updatedModel: ProductOptionsModel): void {
        const model = attrsWhitelist.value.reduce((acc: Record<string, string>, uid: string) => {
            if (updatedModel[uid]) acc[uid] = updatedModel[uid];
            return acc;
        }, {});

        const sku = findFirstSkuByPartialOptions(skus, model);

        if (sku) {
            selectedSku.value = sku;
            return;
        }

        debugConsole.warn('Select Sku by partial props not found... use default (or first) sku');
        selectedSku.value = getSkuByUid(skus, props.item.defaultSkuUid) || skus[0];
    }

    onMounted(() => {
        removeOnceKey(onceKey);
    });

    onBeforeUnmount(() => {
        removeOnceKey(onceKey);
    });
</script>

<style lang="scss"></style>
